import { useState, useEffect, useRef } from 'react';
import App from './App/App';
import Home from './Home/Home';
import { database } from './Firebase/firebaseconfig';
import { USER_ID_KEY } from './Firebase/tracking';
import { ref, set, get, update } from 'firebase/database';

const detectDevice = () => {
  const ua = navigator.userAgent.toLowerCase();
  return /mobile|android|iphone|ipad|ipod/.test(ua) ? 'Mobile' : 'Computer';
};

// iOS is the tricky case: every iOS browser (Chrome, Firefox, Edge, in-app
// webviews like Instagram/Facebook) is really just Safari/WebKit underneath,
// so a naive "contains safari" check would misidentify all of them as
// Safari. Each non-Safari iOS browser does inject its own distinct UA token
// though (CriOS, FxiOS, EdgiOS, ...), so check those FIRST and only fall
// back to "Safari" once none of them match.
const detectBrowser = (ua) => {
  if (/crios\//.test(ua)) return 'Chrome';
  if (/fxios\//.test(ua)) return 'Firefox';
  if (/edgios\//.test(ua)) return 'Edge';
  if (/opios\//.test(ua)) return 'Opera';
  if (/duckduckgo/.test(ua)) return 'DuckDuckGo';
  if (/fban|fbav/.test(ua)) return 'Facebook';
  if (/instagram/.test(ua)) return 'Instagram';
  if (/gsa\//.test(ua)) return 'Google App';
  if (/edg\//.test(ua)) return 'Edge';
  if (/chrome|chromium/.test(ua)) return 'Chrome';
  if (/firefox/.test(ua)) return 'Firefox';
  if (/version\/[\d.]+/.test(ua) && /safari/.test(ua)) return 'Safari';
  if (/safari/.test(ua)) return 'Safari';
  return 'Unknown';
};

const detectOSAndVersion = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('windows')) {
    const m = ua.match(/windows nt ([\d.]+)/);
    return { os: 'Windows', osVersion: m ? m[1] : 'Unknown' };
  }
  if (ua.includes('mac')) {
    const m = ua.match(/mac os x ([\d._]+)/);
    return { os: 'MacOS', osVersion: m ? m[1].replace(/_/g, '.') : 'Unknown' };
  }
  if (ua.includes('android')) {
    const m = ua.match(/android ([\d.]+)/);
    return { os: 'Android', osVersion: m ? m[1] : 'Unknown' };
  }
  if (ua.includes('iphone') || ua.includes('ipad')) {
    const m = ua.match(/os ([\d_]+)/);
    return { os: 'iOS', osVersion: m ? m[1].replace(/_/g, '.') : 'Unknown' };
  }
  return { os: 'Unknown', osVersion: 'Unknown' };
};

// Old "Count" field is left untouched (frozen legacy total) — new visits are
// tallied separately under "New_Count" going forward.
const bumpVisitCount = () => {
  get(ref(database, 'user_visit')).then(snap => {
    const data = snap.exists() ? snap.val() : {};
    const newCount = (data.New_Count || 0) + 1;
    update(ref(database, 'user_visit'), { New_Count: newCount }).catch(() => {});
  }).catch(() => {});
};

// City takes priority; falls back to state/region, then country, if a level is missing.
const buildRegion = (city, region, country) =>
  city ? `${city}, ${region || country || ''}`.replace(/,\s*$/, '') : (region || country || null);

// Personal details (email/phone/username) are intentionally never collected here —
// only anonymous device/location info, used solely for the admin visit analytics dashboard.
const recordUserDetails = () => {
  const device = detectDevice();
  const { os, osVersion } = detectOSAndVersion();
  const userID = Math.floor(10000 + Math.random() * 90000);
  const browserTime = new Date().toLocaleString();
  sessionStorage.setItem(USER_ID_KEY, String(userID));

  const finish = (ip, region) => {
    set(ref(database, `user_info/${userID}`), {
      User_ID: userID,
      IPAddress: ip || 'Unknown',
      Region: region || 'Unknown',
      Device: device,
      OS: os,
      OSVersion: osVersion,
      Timestamp: new Date().toISOString(),
      BrowserTime: browserTime,
      Version: 2,
    }).catch(() => {});
    bumpVisitCount();
  };

  // On iPhone: skip the IP/region lookup for Safari specifically. If the
  // browser can't be positively identified as something else (Chrome,
  // Firefox, etc.), treat it the same as Safari and skip too — safer to
  // under-collect on an unrecognized iPhone browser than risk it being
  // Safari under a UA we didn't account for.
  const ua = navigator.userAgent.toLowerCase();
  const isIPhone = /iphone/.test(ua);
  if (isIPhone) {
    const browser = detectBrowser(ua);
    if (browser === 'Safari' || browser === 'Unknown') {
      finish(null, null);
      return;
    }
  }

  // Primary: ipwho.is (no key, generous free limits). ipapi.co kept as a fallback
  // since it can silently return a rate-limit payload (200 OK, no city data).
  fetch('https://ipwho.is/')
    .then(r => r.json())
    .then(data => {
      if (!data || data.success === false) throw new Error('ipwho.is: ' + (data && data.message));
      finish(data.ip, buildRegion(data.city, data.region, data.country));
    })
    .catch((err1) => {
      console.warn('Geolocation lookup (ipwho.is) failed, trying fallback:', err1);
      fetch('https://ipapi.co/json/')
        .then(r => r.json())
        .then(data => {
          if (!data || data.error) throw new Error('ipapi.co: ' + (data && data.reason));
          finish(data.ip, buildRegion(data.city, data.region, data.country_name));
        })
        .catch((err2) => {
          console.warn('Geolocation lookup (ipapi.co) also failed — recording visit without region:', err2);
          finish(null, null);
        });
    });
};

const VISITED_KEY = 'hasVisitedSquareComputers';

export default function RootView() {
  // Captured once at first render: was the splash already shown earlier this
  // session? If not, the fan-splash plays now and Home is reached THROUGH it —
  // the only case where we want the menu hint to appear.
  const visitedAtLoad = useRef(sessionStorage.getItem(VISITED_KEY) === 'true');
  const [showHome, setShowHome] = useState(visitedAtLoad.current);

  useEffect(() => {
    recordUserDetails();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(VISITED_KEY) === 'true') return;
    const timer = setTimeout(() => {
      sessionStorage.setItem(VISITED_KEY, 'true');
      setShowHome(true);
    }, 6000);
    return () => clearTimeout(timer);
  }, []);

  return showHome ? <Home fromSplash={!visitedAtLoad.current} /> : <App />;
}
